export interface BarcodeFood {
  barcode: string;
  description: string;
  source: string;
}

export interface BarcodeCandidate {
  value: string;
  count: number;
  firstSeenAt: number;
  lastSeenAt: number;
  sources: Set<string>;
}

export interface BarcodeCandidateObservation {
  candidate: BarcodeCandidate;
  confirmed: boolean;
  requiredCount: number;
}

export function normalizeDetectedBarcode(rawValue: string): string | null {
  const value = rawValue.trim();
  if (!/^\d{8,14}$/.test(value)) {
    return null;
  }
  if ([8, 12, 13, 14].includes(value.length) && !isValidGtin(value)) {
    return null;
  }
  return value;
}

export function isValidGtin(value: string): boolean {
  if (!/^\d{8}$|^\d{12,14}$/.test(value)) {
    return false;
  }
  const digits = [...value].map(Number);
  const checkDigit = digits.pop();
  const sum = digits
    .slice()
    .reverse()
    .reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1), 0);
  return checkDigit === (10 - (sum % 10)) % 10;
}

export function registerBarcodeCandidate(
  candidates: Map<string, BarcodeCandidate>,
  value: string,
  source: string,
  observedAt: number
): BarcodeCandidateObservation {
  for (const [candidateValue, candidate] of candidates) {
    if (observedAt - candidate.lastSeenAt > 3_000) {
      candidates.delete(candidateValue);
    }
  }

  const candidate = candidates.get(value) ?? {
    value,
    count: 0,
    firstSeenAt: observedAt,
    lastSeenAt: observedAt,
    sources: new Set<string>()
  };
  candidate.count += 1;
  candidate.lastSeenAt = observedAt;
  candidate.sources.add(source);
  candidates.set(value, candidate);

  const hasDecoderAgreement = candidate.sources.size >= 2;
  const requiredCount = hasDecoderAgreement ? 3 : 5;
  const requiredDuration = hasDecoderAgreement ? 300 : 900;
  return {
    candidate,
    requiredCount,
    confirmed: candidate.count >= requiredCount && candidate.lastSeenAt - candidate.firstSeenAt >= requiredDuration
  };
}

export async function lookupBarcode(barcode: string): Promise<BarcodeFood> {
  const normalized = barcode.replace(/\D/g, "");
  if (!/^\d{8,14}$/.test(normalized)) {
    throw new Error("Il codice deve contenere da 8 a 14 cifre.");
  }
  if (!navigator.onLine) {
    throw new Error("La ricerca del codice a barre richiede una connessione.");
  }
  let response: Response;
  try {
    response = await fetch(`/api/foods/barcode/${normalized}`, { credentials: "include" });
  } catch {
    throw new Error("Servizio codici a barre non raggiungibile.");
  }
  const payload = (await response.json().catch(() => ({}))) as Partial<BarcodeFood> & { detail?: string };
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Accedi al backend per cercare il prodotto.");
    }
    throw new Error(payload.detail || "Prodotto non trovato.");
  }
  if (!payload.description) {
    throw new Error("Descrizione del prodotto non disponibile.");
  }
  return payload as BarcodeFood;
}
